import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

Vue.use(VueRouter);

const isAuthenticated = (to, from, next) => {
    if (store.state.auth.isAuthenticated) {
        next();
        return;
    }
    next("/");
};

/**
 * Клієнтський редірект зі старих query-based URL каталогу на нові SEO-роути.
 * Справжній 301-статус має віддавати інфра (див. catalog-301-redirects.md);
 * це м'який редірект (replace) для користувачів/внутрішніх лінків, щоб не
 * рендерився дублікатний контент по старих URL.
 *
 * Правила (оновлено 2026-06-04 — серія тепер фільтр, не окрема сторінка):
 *   ?brands=X (рівно один бренд) → /brand/X (серія, якщо є, лишається у query)
 *   ?brands=a,b (мульти)         → НЕ редіректимо (закриває noindex)
 *   ?series=Y (без одного бренду) → НЕ редіректимо (серія = query-фільтр)
 *   ?categories=outlet           → /outlet (outlet — категорія, не колекція)
 *   ?sale_banners=:id            → НЕ редіректимо (promotion-режим)
 * Решта query (sort_filter_type, catalog_type, page, атрибутні фільтри) —
 * переноситься на цільову сторінку.
 */
const catalogQueryRedirect = to => {
    const q = to.query || {};
    const lang = to.params.lang;

    const brands =
        typeof q.brands === "string"
            ? q.brands
                  .split(",")
                  .map(s => s.trim())
                  .filter(Boolean)
            : [];

    // 1. Рівно один бренд → /brand/X. Серія (якщо є) лишається query-фільтром.
    //    Мульти-бренд не редіректимо — його закриває noindex.
    if (brands.length === 1) {
        const query = { ...q };
        delete query.brands;
        return { name: "brand", params: { lang, slug: brands[0] }, query };
    }

    // 3. Outlet — окрема сторінка /outlet. На беку outlet — це Category
    //    (?categories=outlet), а не Collection: лише так вмикається аутлетне
    //    сортування цін і коректна видача. categories=outlet шлеться як
    //    locked-параметр у payload, тож із URL його прибираємо.
    if (q.categories === "outlet") {
        const query = { ...q };
        delete query.categories;
        return { name: "outlet", params: { lang }, query };
    }

    return null;
};

const catalogBeforeEnter = (to, from, next) => {
    const redirect = catalogQueryRedirect(to);
    if (redirect) {
        next({ ...redirect, replace: true });
        return;
    }
    next();
};

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/:lang?",
      name: "main",
      component: () => import("@/modules/home/index.vue"),
    },
    {
      path: "/:lang?/catalog",
      name: "products",
      component: () => import("@/modules/catalog-hub/index.vue"),
      props: { pageType: "landing" },
      beforeEnter: catalogBeforeEnter,
    },
    {
      path: "/:lang?/promotions",
      name: "promotions",
      component: () => import("@/modules/promotions/index.vue"),
    },
    {
      path: "/:lang?/promotions/:promotionId",
      name: "promotion",
      component: () => import("@/modules/promotion/index.vue"),
    },
    {
      path: "/:lang?/training-center",
      name: "training",
      component: () => import("@/modules/teaching/training-center/index.vue"),
    },
    {
      path: "/:lang?/training-center/:slug",
      name: "seminars",
      component: () => import("@/modules/teaching/seminars/index.vue"),
    },
    {
      path: "/:lang?/guides",
      name: "guides",
      component: () => import("@/modules/guides/index.vue"),
    },
    {
      path: "/:lang?/cosmobase",
      name: "cosmobase",
      component: () => import("@/modules/cosmobase/index.vue"),
    },
    {
      path: "/:lang?/card/:slug",
      name: "card",
      component: () => import("@/modules/card/index.vue"),
    },

    {
      path: "/:lang?/server-maintenance",
      name: "server-maintenance",
      component: () => import("@/modules/system/server-maintenance/index.vue"),
    },
    {
      path: "/:lang?/server-error",
      name: "server-error",
      component: () => import("@/modules/system/server-error/index.vue"),
    },
    {
      path: "/:lang?/brand/:slug",
      name: "brand",
      component: () => import("@/modules/catalog-hub/index.vue"),
      props: route => ({ pageType: "brand", slug: route.params.slug }),
    },
    // Outlet — окрема сторінка зі своїм pageType. На беку outlet — Category
    // (?categories=outlet), тож тут НЕ колекція: без locked collection і без серій.
    {
      path: "/:lang?/outlet",
      name: "outlet",
      component: () => import("@/modules/catalog-hub/index.vue"),
      props: { pageType: "outlet" },
    },
    // Старий URL outlet (/collection/outlet) 301-редіректимо на /outlet, щоб не
    // втратити індекс. Має стояти ПЕРЕД /collection/:slug (матч за порядком).
    {
      path: "/:lang?/collection/outlet",
      redirect: to => ({ name: "outlet", params: { lang: to.params.lang }, query: to.query }),
    },
    {
      path: "/:lang?/collection/:slug",
      name: "collection",
      component: () => import("@/modules/catalog-hub/index.vue"),
      props: route => ({ pageType: "collection", slug: route.params.slug }),
    },
    // Серії тепер фільтр, а не сторінка. Старі SEO-URL 301-редіректимо на
    // відповідну посадкову з серією у query, щоб не втратити індекс.
    {
      path: "/:lang?/collection/:collection/series/:slug",
      redirect: to => ({
        name: "collection",
        params: { lang: to.params.lang, slug: to.params.collection },
        query: { ...to.query, series: to.params.slug },
      }),
    },
    {
      path: "/:lang?/series/:slug",
      redirect: to => ({
        name: "products",
        params: { lang: to.params.lang },
        query: { ...to.query, series: to.params.slug },
      }),
    },
    {
      path: "*",
      name: "page-not-found",
      component: () => import("@/modules/system/page-not-found/index.vue"),
    },
    {
      path: "/:lang?/results",
      name: "result",
      component: () => import("@/modules/search-catalog/index.vue"),
    },
    {
      path: "/:lang?/buy-flow",
      name: "buy-flow",
      redirect: "/:lang?/buy-flow/basket",
      component: () => import("@/modules/buy-flow/index.vue"),
      children: [
        {
          path: "basket",
          name: "buy-flow-basket",
          component: () => import("@/modules/buy-flow/pages/basket/index.vue"),
          meta: {
            title: "Корзина",
          },
        },
        {
          path: "order",
          name: "buy-flow-order",
          component: () => import("@/modules/buy-flow/pages/order/index.vue"),
          meta: {
            title: "Оформлення замовлення",
          },
        },
      ],
    },
    //--------------------------------------collaboration--------------------------------------
    {
      path: "/:lang?/pages/spivpracya",
      name: "collaboration",
      component: () => import("../modules/pages/collaboration/index.vue"),
    },
    //--------------------------------------before-after--------------------------------------
    {
      path: "/:lang?/before-after",
      name: "before-after",
      component: () => import("../modules/pages/before-after/index.vue"),
    },
    {
      path: "/:lang?/before-after/:category",
      name: "before-after-category",
      component: () => import("../modules/pages/before-after-category/index.vue"),
      props: true,
    },
    {
      path: "/:lang?/before-after/:category/:case",
      name: "before-after-catalog",
      component: () => import("../modules/pages/before-after-catalog/index.vue"),
      props: true,
    },
    //--------------------------------------news--------------------------------------
    {
      path: "/:lang?/news",
      name: "news",
      component: () => import("@/modules/pages/page-main/index.vue"),
      meta: { isNews: true, isNewsList: true },
    },
    {
      path: "/:lang?/news/:slug",
      name: "news-single",
      component: () => import("@/modules/pages/page-main/index.vue"),
      meta: { isNews: true },
    },
    //--------------------------------------pages--------------------------------------
    {
      path: "/:lang?/pages/:slug",
      name: "page",
      component: () => import("@/modules/pages/page-main/index.vue"),
      beforeEnter: (to, from, next) => {
        // Старі URL новин → нові SEO-роути. Реальний 301 має ставити інфра.
        if (to.params.slug === "scientific-news") {
          next({ name: "news", params: { lang: to.params.lang }, replace: true });
          return;
        }
        if (to.query.type === "new") {
          const query = { ...to.query };
          delete query.type;
          next({
            name: "news-single",
            params: { lang: to.params.lang, slug: to.params.slug },
            query,
            replace: true,
          });
          return;
        }
        next();
      },
    },
    {
      path: "/:lang?/pages/:slugPage/:slugIngredient",
      name: "sub-page",
      component: () => import("@/modules/pages/sub-page/index.vue"),
    },
    {
      path: "/:lang?/contacts",
      name: "contact-page",
      component: () => import("@/modules/pages/contacts/index.vue"),
    },
    //--------------------------------------pages--------------------------------------
    {
      path: "/:lang?/pigment-code",
      name: "PigmentCode",
      component: () => import("@/modules/pigment-code/index.vue"),
    },
    {
      path: "/:lang?/forum",
      name: "ProAge",
      component: () => import("@/modules/pro-age/index.vue"),
    },
    // {
    //   path: "/:lang?/forum",
    //   name: "forum",
    //   component: () => import("@/modules/teaching/forum/index.vue"),
    // },
    {
      path: "/:lang?/reviews",
      name: "ReviewsPage",
      component: () => import("@/modules/reviews-page/index.vue"),
    },
    {
      path: "/:lang?/faq",
      name: "faq",
      component: () => import("@/modules/pages/faq/index.vue"),
    },
    {
      path: "/:lang?/cosmobase-course/:id",
      name: "cosmobase-course",
      component: () => import("@/modules/pages/cosmobase-course/index.vue"),
    },
    {
      path: "/:lang?/cosmobase-materials/:id",
      name: "cosmobase-materials",
      component: () => import("@/modules/pages/cosmobase-materials/index.vue"),
    },
    {
      path: "/:lang?/cosmobase-lesson/:id/:lessonId",
      name: "cosmobase-lesson",
      component: () => import("@/modules/pages/cosmobase-lesson/index.vue"),
    },
    {
      path: "/:lang?/delivery",
      name: "delivery",
      component: () => import("@/modules/pages/delivery-payment/index.vue"),
    },
    {
      path: "/:lang?/profile",
      redirect: "/:lang?/profile/user-info",
      component: () => import("@/modules/profile/index.vue"),
      name: "profile",
      beforeEnter: isAuthenticated,
      children: [
        {
          path: "user-info",
          name: "profile-user-info",
          component: () => import("@/modules/profile/pages/user-info/index.vue"),
        },
        {
          path: "user-professional",
          name: "profile-user-professional",
          component: () => import("@/modules/profile/pages/user-professional/index.vue"),
        },
        {
          path: "profile-valentines-day",
          name: "user-valentine",
          component: () => import("@/modules/profile/pages/profile-valentines-day/index.vue"),
        },
        {
          path: "user-current-orders",
          name: "profile-user-current-orders",
          component: () => import("@/modules/profile/pages/current-orders/index.vue"),
        },
        {
          path: "user-history-orders",
          name: "profile-user-history-orders",
          component: () => import("@/modules/profile/pages/history-orders/index.vue"),
        },
        {
          path: "managers",
          name: "managers",
          meta: { title: "менеджери" },
          component: () => import("@/modules/profile/pages/managers/index.vue"),
        },
        {
          path: "cosmobase",
          name: "cosmobase-profile",
          component: () => import("@/modules/profile/pages/cosmobase/index.vue"),
        },
        {
          path: "platform",
          name: "platform",
          component: () => import("@/modules/profile/pages/platform/index.vue"),
          redirect: "/:lang?/profile/platform/seminars",
          children: [
            {
              path: "seminars",
              name: "platform-seminars",
              component: () => import("@/modules/profile/pages/platform/pages/seminars/index.vue"),
            },
            {
              path: "webinar/:id",
              name: "webinar",
              component: () => import("@/modules/profile/pages/platform/pages/webinars/index.vue"),
            },
            {
              path: "webinar/:id/quiz",
              name: "quiz",
              component: () => import("@/modules/profile/pages/platform/pages/test/index.vue"),
            },
            {
              path: "webinar/:id/quiz-result",
              name: "quiz-result",
              component: () => import("@/modules/profile/pages/platform/pages/test-result/index.vue"),
            },
          ],
        },
        {
          path: "user-favorites",
          name: "profile-user-favorites",
          component: () => import("@/modules/profile/pages/favorites/index.vue"),
        },
        {
          path: "user-professional-club",
          name: "user-professional-club",
          redirect: "user-professional-club/clients-list",

          component: () => import("@/modules/profile/pages/professional-club/index.vue"),
          children: [
            {
              path: "clients-list",
              name: "profile-clients-list",
              component: () => import("@/modules/profile/pages/professional-club/pages/clients-list/index.vue"),
            },
            {
              path: "client-history/:id",
              name: "profile-client-history",
              component: () => import("@/modules/profile/pages/professional-club/pages/client-history/index.vue"),
            },
          ],
        },
        {
          path: "distributor-orders",
          name: "distributor-orders",
          redirect: "distributor-orders/moving",

          component: () => import("@/modules/profile/pages/distributor-orders/index.vue"),
          children: [
            {
              path: "moving",
              name: "moving",
              meta: { title: "Переміщення на склад" },
              component: () =>
                import("@/modules/profile/pages/distributor-orders/pages/distributor-orders-moving/index.vue"),
            },
            {
              path: "invoice",
              name: "invoice",
              meta: { title: "Видаткові накладні" },

              component: () =>
                import("@/modules/profile/pages/distributor-orders/pages/distributor-orders-invoice/index.vue"),
            },
          ],
        },
        {
          path: "distributor-storage",
          name: "distributor-storage",
          redirect: "distributor-storage/leftovers",

          component: () => import("@/modules/profile/pages/distributor-storage/index.vue"),
          children: [
            {
              path: "leftovers",
              name: "leftovers",

              component: () =>
                import("@/modules/profile/pages/distributor-storage/pages/distributor-storage-leftovers/index.vue"),
            },
            {
              path: "sales",
              name: "sales",
              meta: { title: "Продажі зі складу" },

              component: () =>
                import("@/modules/profile/pages/distributor-storage/pages/distributor-storage-sales/index.vue"),
            },
            {
              path: "report",
              name: "report",

              component: () =>
                import("@/modules/profile/pages/distributor-storage/pages/distributor-storage-report/index.vue"),
            },
          ],
        },
      ],
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    let appElement = document.querySelector(".app");
    if (to.path !== from.path) {
      if (savedPosition) {
        return savedPosition;
      } else {
        appElement.scrollTop = 0;
        return { x: 0, y: 0 };
      }
    }
  },
});

export default router;
